import React, { useEffect, useState, useCallback, memo } from 'react';
import { View, FlatList, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getReservationHistoryData } from '../../../api/Reservation';
import { useAlertModal } from '../../../common/hooks';
import { msgStr } from '../../../common/constants/Message';

interface RevisionChange {
  path: string;
  diff: string;
  document: {
    kind: string;
    path: string[];
    lhs?: any;
    rhs?: any;
  };
}

interface VersionHistory {
  id: string;
  version: number;
  timestamp: string;
  operation: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  changes: Record<string, any>;
  documentId: string;
  meta?: any;
  revisionChanges?: RevisionChange[];
  model?: string; 
}

interface ReservationItem {
  reservation_id: number;
  display_name: string;
  quantity: number;
  family_id: number;
  price_group_id: string;
  price: number;
  size?: string;
}

interface Props {
  reservationInfo: any;
}

const EventsTab = ({ reservationInfo }: Props) => {
  const [historyData, setHistoryData] = useState<VersionHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Record<string, boolean>>({});
  const { showAlert } = useAlertModal();
  
  const fetchVersionHistory = useCallback(() => {
    if (!reservationInfo?.order_number) return;
    
    setIsLoading(true);
    getReservationHistoryData({ order_number: reservationInfo.order_number }, (jsonRes, status, error) => {
      setIsLoading(false);
      switch (status) {
        case 200:
          setHistoryData(jsonRes || []);
          break;
        default:
          setHistoryData([]);
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  }, [reservationInfo?.order_number, showAlert]);

  useEffect(() => {
    fetchVersionHistory();
  }, [fetchVersionHistory]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' @ ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date fields for better readability
  const formatDateValue = (value: string) => {
    try {
      // Check if the value is a valid ISO date string
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value) || 
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value) ||
          /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const date = new Date(value);
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }
      return value;
    } catch (e) {
      return value;
    }
  };

  const getOperationLabel = (operation: string) => {
    switch (operation?.toLowerCase()) {
      case 'create':
        return 'Created';
      case 'update':
        return 'Updated';
      case 'delete':
        return 'Deleted';
      case 'cancel':
        return 'Cancelled';
      default:
        return operation || 'Modified';
    }
  };

  const getOperationColor = (operation: string) => {
    switch (operation?.toLowerCase()) {
      case 'create':
        return '#28a745'; // green
      case 'update':
        return '#ffc107'; // yellow
      case 'delete':
        return '#dc3545'; // red
      case 'cancel':
        return '#6c2dc7'; // purple for cancel operations
      default:
        return '#6c757d'; // gray
    }
  };

  const getOperationIcon = (operation: string) => {
    switch (operation?.toLowerCase()) {
      case 'create':
        return 'plus-circle';
      case 'update':
        return 'edit';
      case 'delete':
        return 'trash-alt';
      case 'cancel':
        return 'ban'; // Changed icon for cancel operations
      default:
        return 'history';
    }
  };

  const toggleVersionExpand = (versionId: string) => {
    setExpandedVersions(prev => ({
      ...prev,
      [versionId]: !prev[versionId]
    }));
  };

  // Format field name to be more readable
  const formatFieldName = (field: string, model?: string) => {
    if (model === 'ReservationItems') {
      switch (field) {
        case 'display_name':
          return 'Item Name';
        case 'quantity':
          return 'Quantity';
        case 'price':
          return 'Price';
        case 'family_id':
          return 'Item Family';
        case 'price_group_id':
          return 'Price Group';
        case 'size':
          return 'Size';
        case 'reservation_id':
          return 'Reservation ID';
        default:
          break;
      }
    }

    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderChangeItem = (change: RevisionChange, model?: string) => {
    // Skip if no document or path
    if (!change.document || !change.path) return null;
    
    const field = change.path;
    let oldValue = change.document.lhs;
    let newValue = change.document.rhs;
    
    // Format values based on type
    const isDateField = field.toLowerCase().includes('date') || 
                       ['start_date', 'end_date', 'created_at', 'updated_at'].includes(field);
    
    // Format old value
    if (oldValue === null || oldValue === undefined) {
      oldValue = '(none)';
    } else if (oldValue === '') {
      oldValue = '(empty)';
    } else if (typeof oldValue === 'boolean') {
      oldValue = oldValue ? 'Yes' : 'No';
    } else if (isDateField && typeof oldValue === 'string') {
      oldValue = formatDateValue(oldValue);
    }
    
    // Format new value
    if (newValue === null || newValue === undefined) {
      newValue = '(none)';
    } else if (newValue === '') {
      newValue = '(empty)';
    } else if (typeof newValue === 'boolean') {
      newValue = newValue ? 'Yes' : 'No';
    } else if (isDateField && typeof newValue === 'string') {
      newValue = formatDateValue(newValue);
    }
    
    if (model === 'ReservationItems') {
      if (field === 'display_name' && change.document.kind === 'E' && (!oldValue || oldValue === '(none)')) {
        return (
          <View style={[styles.changeItemContainer]} key={field}>
            <Text style={styles.changeText}>
              Added item: <Text style={styles.fieldValue}>{String(newValue || '')}</Text>
            </Text>
          </View>
        );
      }
      
      if (field === 'quantity') {
        return (
          <View style={[styles.changeItemContainer]} key={field}>
            <Text style={styles.changeText}>
              Changed <Text style={styles.fieldValue}>Quantity</Text> from{' '}
              <Text style={styles.oldValue}>{String(oldValue)}</Text> →{' '}
              <Text style={styles.newValue}>{String(newValue)}</Text>
            </Text>
          </View>
        );
      }
      
      if (field === 'price') {
        return (
          <View style={[styles.changeItemContainer]} key={field}>
            <Text style={styles.changeText}>
              Changed <Text style={styles.fieldValue}>Price</Text> from{' '}
              <Text style={styles.oldValue}>${String(oldValue)}</Text> →{' '}
              <Text style={styles.newValue}>${String(newValue)}</Text>
            </Text>
          </View>
        );
      }
      
      return (
        <View style={[styles.changeItemContainer]} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldValue}>{formatFieldName(field, model)}</Text> from{' '}
            <Text style={styles.oldValue}>{String(oldValue)}</Text> →{' '}
            <Text style={styles.newValue}>{String(newValue)}</Text>
          </Text>
        </View>
      );
    }
    
    if (field === 'stripe_cus_id') {
      if (newValue && (!oldValue || oldValue === '(none)')) {
        return (
          <View style={styles.changeItemContainer} key={field}>
            <Text style={styles.changeText}>
              Added user: <Text style={styles.fieldValue}>Stripe Account</Text>
            </Text>
          </View>
        );
      } else if (!newValue && oldValue && oldValue !== '(none)') {
        return (
          <View style={styles.changeItemContainer} key={field}>
            <Text style={styles.changeText}>
              Removed: <Text style={styles.fieldValue}>Stripe Account</Text>
            </Text>
          </View>
        );
      } else if (oldValue && newValue && oldValue !== newValue && oldValue !== '(none)' && newValue !== '(none)') {
        return (
          <View style={styles.changeItemContainer} key={field}>
            <Text style={styles.changeText}>
              Updated: <Text style={styles.fieldValue}>Stripe Account</Text>
            </Text>
          </View>
        );
      }
      return null;
    }
    
    // Special handling for order number (creation event)
    if (field === 'order_number') {
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            New order created: <Text style={styles.fieldValue}>{String(newValue || '')}</Text>
          </Text>
        </View>
      );
    }
    
    // Special handling for stage changes
    if (field === 'stage') {
      const stageLabels: Record<number, string> = {
        1: 'Provisional',
        2: 'Confirmed',
        3: 'Checked out',
        4: 'Checked in',
        5: 'Cancelled'
      };
      
      // Format stage values
      oldValue = oldValue !== null && oldValue !== undefined ? Number(oldValue) : null;
      newValue = newValue !== null && newValue !== undefined ? Number(newValue) : null;
      
      const oldStageLabel = oldValue !== null ? stageLabels[oldValue as number] || `Unknown (${oldValue})` : '(none)';
      const newStageLabel = newValue !== null ? stageLabels[newValue as number] || `Unknown (${newValue})` : '(none)';
      
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldLabel}>Status</Text> from{' '}
            <Text style={styles.oldValue}>{oldStageLabel}</Text> →{' '}
            <Text style={styles.newValue}>{newStageLabel}</Text>
          </Text>
        </View>
      );
    }
    
    // Special handling for phone number updates
    if (field === 'phone' || field === 'phone_number' || field.includes('phone')) {
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldLabel}>Phone Number</Text> from{' '}
            <Text style={styles.oldValue}>{oldValue === '' ? '(empty)' : String(oldValue || '(none)')}</Text> →{' '}
            <Text style={styles.newValue}>{newValue === '' ? '(empty)' : String(newValue || '(none)')}</Text>
          </Text>
        </View>
      );
    }

    // Special handling for delivery related fields
    if (field === 'delivery_by' || field.includes('deliver_by')) {
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldLabel}>Deliver By</Text> from{' '}
            <Text style={styles.oldValue}>{oldValue === '' ? '(empty)' : String(oldValue || '(none)')}</Text> →{' '}
            <Text style={styles.newValue}>{newValue === '' ? '(empty)' : String(newValue || '(none)')}</Text>
          </Text>
        </View>
      );
    }
    
    // Special handling for pickup related fields
    if (field === 'pickup' || field.includes('pick_up') || field.includes('pickup')) {
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldLabel}>Pickup</Text> from{' '}
            <Text style={styles.oldValue}>{oldValue === '' ? '(empty)' : String(oldValue || '(none)')}</Text> →{' '}
            <Text style={styles.newValue}>{newValue === '' ? '(empty)' : String(newValue || '(none)')}</Text>
          </Text>
        </View>
      );
    }
    
    // Special handling for textSent field
    if (field === 'textSent') {
      return (
        <View style={styles.changeItemContainer} key={field}>
          <Text style={styles.changeText}>
            Changed <Text style={styles.fieldLabel}>SMS sent</Text> from{' '}
            <Text style={styles.oldValue}>{oldValue === '' ? '(empty)' : String(oldValue || '(none)')}</Text> →{' '}
            <Text style={styles.newValue}>{newValue === '' ? '(empty)' : String(newValue || '(none)')}</Text>
          </Text>
        </View>
      );
    }
    
    // Default handling for other fields
    return (
      <View style={styles.changeItemContainer} key={field}>
        <Text style={styles.changeText}>
          Changed <Text style={styles.fieldLabel}>{formatFieldName(field, model)}</Text> from{' '}
          <Text style={styles.oldValue}>{String(oldValue)}</Text> →{' '}
          <Text style={styles.newValue}>{String(newValue)}</Text>
        </Text>
      </View>
    );
  };

  const renderVersionItem = ({ item: version }) => {
    const isExpanded = expandedVersions[version.id] || false;
    
    // Check if this is a cancellation (stage change to 5)
    let operation = version.operation;
    if (operation?.toLowerCase() === 'update') {
      // Check if this update is actually a cancellation (stage changed to 5)
      const stageChange = version.revisionChanges?.find(change => 
        change.path === 'stage' && change.document?.rhs === 5
      );
      
      if (stageChange) {
        operation = 'cancel'; // Override operation type for UI purposes
      }
    }
    
    const operationColor = getOperationColor(operation);
    const operationIcon = getOperationIcon(operation);
    const revisionChanges = version.revisionChanges || [];
    const changesCount = revisionChanges.length;
    const operationLabel = getOperationLabel(operation);
    const isReservationItem = version.model === 'ReservationItems';
    
    return (
      <View style={styles.versionContainer}>
        <TouchableOpacity 
          style={styles.versionHeader}
          onPress={() => toggleVersionExpand(version.id)}
          activeOpacity={0.7}
        >
          <View style={styles.headerContent}>
            <View style={[styles.operationDot, { backgroundColor: operationColor }]}>
              <FontAwesome5 
                name={isReservationItem ? 'box' : operationIcon} 
                size={16} 
                color="white" 
              />
            </View>
            <Text style={isReservationItem ? styles.itemText : styles.operationText}>
              {isReservationItem && operation.toLowerCase() === 'create' ? '[Item Added]' : isReservationItem ? '[Item ' + operationLabel + ']' : '[' + operationLabel + ']'} {formatTimestamp(version.timestamp)}
              {version.userName || version.meta?.userName ? 
                <Text style={styles.byUser}> — by {version.userName || version.meta?.userName}</Text> : 
                null
              }
            </Text>
          </View>
          <FontAwesome5 
            name={isExpanded ? "chevron-down" : "chevron-right"} 
            size={16} 
            color="#666" 
            style={styles.expandIcon}
          />
        </TouchableOpacity>
        
        {isExpanded && (
          <View style={styles.changesContainer}>
            {operation === 'create' && !isReservationItem && (
              <>
                {revisionChanges.find(change => change.path === 'order_number') && (
                  renderChangeItem(revisionChanges.find(change => change.path === 'order_number')!, version.model)
                )}
                
                {revisionChanges
                  .filter(change => 
                    ['start_date', 'end_date', 'email'].includes(change.path || '')
                  )
                  .map((change) => renderChangeItem(change, version.model))
                }
              </>
            )}
            
            {operation === 'create' && isReservationItem && (
              <>
                {revisionChanges.find(change => change.path === 'display_name') && (
                  renderChangeItem(revisionChanges.find(change => change.path === 'display_name')!, version.model)
                )}
                
                {revisionChanges
                  .filter(change => 
                    ['quantity', 'price'].includes(change.path || '')
                  )
                  .map((change) => renderChangeItem(change, version.model))
                }
              </>
            )}
            
            {operation !== 'create' && changesCount > 0 ? (
              revisionChanges.map((change) => renderChangeItem(change, version.model))
            ) : operation !== 'create' && (
              <Text style={styles.noChangesText}>No detailed changes available</Text>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerLabel}>Log Entries</Text>
      <Text style={styles.historyCount}>
        {historyData.length} version{historyData.length !== 1 ? 's' : ''}
      </Text>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyState}>
      <FontAwesome5 name="history" size={48} color="#ccc" />
      <Text style={styles.emptyText}>No version history available</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <FlatList
        data={historyData}
        renderItem={renderVersionItem}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={styles.flatListContent}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        style={styles.flatList}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white', // Changed back to white background
  },
  flatList: {
    flex: 1,
    width: '100%',
  },
  flatListContent: {
    padding: 16,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333', // Changed to dark color for white background
  },
  historyCount: {
    fontSize: 14,
    color: '#666', // Adjusted for white background
  },
  versionContainer: {
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: '#cccccc',
    paddingLeft: 12,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    padding: 8,
  },
  itemContainer: {
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: '#5c7cfa',
    paddingLeft: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    padding: 8,
  },
  versionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  operationDot: {
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  operationText: {
    fontSize: 16,
    color: '#333', // Changed to dark color for white background
    flex: 1,
  },
  itemText: {
    fontSize: 16,
    color: '#495057',
    flex: 1,
    fontWeight: '500',
  },
  byUser: {
    fontStyle: 'italic',
    color: '#777', // Adjusted for white background
  },
  expandIcon: {
    padding: 5,
  },
  changesContainer: {
    marginTop: 8,
    marginLeft: 34, // Align with the text after the dot
    paddingBottom: 8,
  },
  changeItemContainer: {
    marginBottom: 12,
    paddingLeft: 12,
    position: 'relative',
  },
  changeItemBar: {
    position: 'absolute',
    left: 0,
    top: 10,
    width: 3,
    height: 16,
    backgroundColor: '#5c7cfa',
  },
  changeText: {
    fontSize: 15,
    color: '#333', // Changed to dark color for white background
    lineHeight: 22,
    paddingVertical: 4,
  },
  fieldLabel: {
    fontWeight: 'bold',
    color: '#000', // Changed to black for white background
  },
  fieldValue: {
    fontWeight: '500',
    color: '#000', // Changed to black for white background
  },
  oldValue: {
    color: '#ff6b6b',
    textDecorationLine: 'line-through',
  },
  newValue: {
    color: '#69db7c',
  },
  noChangesText: {
    fontSize: 14,
    color: '#666', // Adjusted for white background
    fontStyle: 'italic',
    paddingVertical: 8,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    minHeight: 200,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666', // Adjusted for white background
  },
});

export default EventsTab;
